import { Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAtomValue } from 'jotai';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { DataCard } from '../../../../../components/Card/DataCard';
import { LabeledFieldContainer } from '../../../../../components/LabeledField';
import { Loader } from '../../../../../components/Loader/Loader';
import { IScenarioWeightedRunResponseDataModel } from '../../../../../data-models/scenario-run.data-model';
import { runWeightedScenarios } from '../../../../../services/queries/MaggieScenarioQueries';
import { FMT } from '../../../../../util/formatter-service';
import { SubSection } from '../../../Summary/components/SubSection';
import { NoDataMessage2 } from '../../../components/Messages/HelpMessage';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import { MainContainerWithAlert } from '../../components/commonStyledComponents';

/**
 * Drill-in for the Weighted Outcome — the same full-page pattern as a single scenario
 * run (re-runs on mount, KPI cards + detail section), showing the blended metrics and
 * the probability-weighted cash-flow series.
 */
export const WeightedOutcomeVisualization: FC = () => {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const navigate = useNavigate();
  const [result, setResult] = useState<IScenarioWeightedRunResponseDataModel | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId <= 0) {
      return;
    }
    let active = true;
    setLoading(true);
    runWeightedScenarios(companyId)
      .then((data) => active && setResult(data))
      .catch((err) => {
        console.error(err);
        if (active) {
          setResult(null);
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [companyId]);

  if (loading) {
    return <Loader />;
  }

  return (
    <MainContainerWithAlert>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ marginBottom: '1rem' }}>
        <Typography variant='h6'>Weighted Outcome</Typography>
        <Button
          variant='outlined'
          color='secondary'
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Back to scenarios
        </Button>
      </Stack>
      {!result ? (
        <NoDataMessage2 />
      ) : (
        <>
          <SubSection title={'Key Performance Indicators'} noCard={true}>
            <ul css={LabeledFieldContainer} style={{ gridTemplateColumns: 'repeat(auto-fit, 12.5rem)' }}>
              <DataCard
                title='Exit Value'
                formattedValue={FMT.format(
                  'currencyShort',
                  result.weightedOutcome.grossProceeds != null
                    ? result.weightedOutcome.grossProceeds
                    : result.weightedOutcome.fmv
                )}
              />
              <DataCard
                title='MOIC'
                formattedValue={FMT.format('multiplier', result.weightedOutcome.grossMoic)}
              />
              <DataCard
                title='IRR'
                formattedValue={result.weightedIrr == null ? '—' : FMT.format('percent', result.weightedIrr)}
              />
            </ul>
          </SubSection>

          <SubSection title={'Blended Cash Flows'} noCard={true}>
            <Table size='small' aria-label='blended cash flows'>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align='right'>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.blendedCashFlows.map((point) => (
                  <TableRow key={point.date}>
                    <TableCell>{point.date}</TableCell>
                    <TableCell align='right'>{FMT.format('currencyShort', point.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SubSection>
        </>
      )}
    </MainContainerWithAlert>
  );
};
